<template>
    <main class="invitation-page">
        <section class="invitation-card" aria-labelledby="invitation-title">
            <header class="invitation-hero">
                <div class="invitation-mark" aria-hidden="true">
                    <i class="material-icons-round">diversity_3</i>
                </div>
                <p class="invitation-eyebrow">A PLACE FOR YOU</p>
                <h1 id="invitation-title">Serve better, together.</h1>
                <p class="invitation-intro">
                    You have been invited to connect with a People Group and share the joy of serving.
                </p>

                <div v-if="invitation" class="invitation-place">
                    <div class="place-detail">
                        <span class="place-label">Organization</span>
                        <strong>{{ invitation.organization?.name || "Your organization" }}</strong>
                    </div>
                    <div class="place-divider" aria-hidden="true"></div>
                    <div class="place-detail">
                        <span class="place-label">Location</span>
                        <strong>{{ invitation.location?.name || "Your Location" }}</strong>
                    </div>
                </div>
            </header>

            <div class="invitation-content">
                <div v-if="loading" class="invitation-loading" role="status">
                    <span class="spinner-border spinner-border-sm text-success" aria-hidden="true"></span>
                    <span>Preparing your invitation...</span>
                </div>

                <div v-else-if="errorMessage" class="alert alert-danger" role="alert">
                    <p class="mb-1">{{ errorMessage }}</p>
                    <p v-if="switchAccountRequired && invitation" class="mb-3 small">
                        This invitation was sent to <strong>{{ invitation.email }}</strong>. Sign in with that email address to accept it.
                    </p>
                    <button
                        v-if="switchAccountRequired"
                        type="button"
                        class="btn btn-outline-danger btn-sm mb-0"
                        :disabled="switchingAccount"
                        @click="switchToInvitedAccount"
                    >
                        {{ switchingAccount ? "Signing out..." : "Sign in with the invited email" }}
                    </button>
                </div>

                <template v-else-if="invitation && !accepted">
                    <div class="group-invitation">
                        <span class="group-invitation-label">Your invitation is for</span>
                        <h2>{{ invitation.group.name }}</h2>
                        <span class="group-type">{{ formatGroupType(invitation.group.people_group_type) }} group</span>
                    </div>

                    <p class="invitation-copy">
                        Join this group to coordinate with people you serve alongside. Your schedule remains yours to manage unless you choose otherwise.
                    </p>

                    <label class="consent-option">
                        <input v-model="allowAdminManagement" class="form-check-input" type="checkbox">
                        <span>
                            <strong>Let group admins help manage my schedule and availability</strong>
                            <small>You can leave this unchecked and still join. You can change this permission later.</small>
                        </span>
                    </label>

                    <button
                        type="button"
                        class="btn btn-primary join-button mb-0"
                        :disabled="saving"
                        @click="acceptInvitation"
                    >
                        <span v-if="saving" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                        {{ saving ? "Joining your group..." : "Accept invitation" }}
                    </button>
                    <p class="privacy-note mb-0">
                        <i class="material-icons-round" aria-hidden="true">lock</i>
                        Your schedule-sharing choice is always yours.
                    </p>
                </template>

                <div v-else-if="accepted" class="accepted-state" role="status">
                    <div class="accepted-icon" aria-hidden="true">
                        <i class="material-icons-round">check_circle</i>
                    </div>
                    <p class="accepted-eyebrow">YOU'RE IN</p>
                    <h2>Welcome to {{ invitation?.group?.name }}!</h2>
                    <p>
                        {{ allowAdminManagement
                            ? "You joined the group and its admins may help manage your schedule and availability."
                            : "You joined the group. Your schedule and availability remain private from group admins." }}
                    </p>
                    <button type="button" class="btn btn-primary mb-0" @click="router.push({ name: '/' })">
                        <i class="material-icons-round align-middle me-1" aria-hidden="true">login</i>
                        Sign in to ChurchScheduling
                    </button>
                </div>
            </div>
        </section>
        <p class="invitation-footer">ChurchScheduling · Serving is better together</p>
    </main>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import api from "@/config/api";

const route = useRoute();
const router = useRouter();
const store = useStore();
const token = String(route.query.token || "");
const invitation = ref(null);
const allowAdminManagement = ref(false);
const accepted = ref(false);
const loading = ref(true);
const saving = ref(false);
const switchingAccount = ref(false);
const switchAccountRequired = ref(false);
const errorMessage = ref("");

const loadInvitation = async () => {
    if (!token) {
        errorMessage.value = "This invitation link is missing its token.";
        loading.value = false;
        return;
    }
    if (!localStorage.getItem("token")) {
        await router.replace({ name: "SignIn", query: { redirect: route.fullPath } });
        return;
    }
    try {
        const { data } = await api.get(`/people-groups/invitations/${encodeURIComponent(token)}`);
        invitation.value = data.data;
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "This invitation is invalid or has expired.";
    } finally {
        loading.value = false;
    }
};

const acceptInvitation = async () => {
    saving.value = true;
    errorMessage.value = "";
    switchAccountRequired.value = false;
    try {
        await api.post(`/people-groups/invitations/${encodeURIComponent(token)}/accept`, {
            allow_admin_management: allowAdminManagement.value,
        });
        accepted.value = true;
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "The invitation could not be accepted.";
        switchAccountRequired.value = error?.response?.status === 403
            && errorMessage.value === "Sign in with the email address this invitation was sent to.";
    } finally {
        saving.value = false;
    }
};

const switchToInvitedAccount = async () => {
    switchingAccount.value = true;
    try {
        await store.dispatch("logout");
        store.commit("SET_SIDEBAR_ROUTES", null);
        localStorage.clear();
        await router.replace({ name: "SignIn", query: { redirect: route.fullPath } });
    } catch (error) {
        console.error("Could not switch accounts for the People Group invitation:", error);
        errorMessage.value = "We couldn't sign you out automatically. Please sign out from the account menu, then open this invitation again.";
        switchAccountRequired.value = false;
    } finally {
        switchingAccount.value = false;
    }
};

const formatGroupType = (type) => {
    const normalized = String(type || "").trim();
    return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "People";
};

onMounted(loadInvitation);
</script>

<style scoped>
.invitation-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px 24px;
    background:
        radial-gradient(circle at 12% 10%, rgba(187, 247, 208, 0.6), transparent 28%),
        radial-gradient(circle at 90% 88%, rgba(191, 219, 254, 0.55), transparent 30%),
        #f4f7f5;
}

.invitation-card {
    width: min(100%, 720px);
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.07);
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12);
}

.invitation-hero {
    padding: 40px clamp(24px, 6vw, 56px) 34px;
    color: #fff;
    text-align: center;
    background: linear-gradient(130deg, #123f37 0%, #167354 52%, #158a72 100%);
}

.invitation-mark,
.accepted-icon {
    display: grid;
    width: 58px;
    height: 58px;
    margin: 0 auto 16px;
    place-items: center;
    border-radius: 18px;
    color: #d1fae5;
    background: rgba(255, 255, 255, 0.15);
}

.invitation-mark .material-icons-round,
.accepted-icon .material-icons-round {
    font-size: 32px;
}

.invitation-eyebrow,
.accepted-eyebrow {
    margin: 0 0 8px;
    color: #bbf7d0;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.16em;
}

.invitation-hero h1 {
    margin: 0;
    color: #fff;
    font-size: clamp(1.8rem, 5vw, 2.45rem);
    font-weight: 750;
    letter-spacing: -0.035em;
}

.invitation-intro {
    max-width: 460px;
    margin: 12px auto 0;
    color: rgba(255, 255, 255, 0.83);
    font-size: 0.98rem;
    line-height: 1.6;
}

.invitation-place {
    display: grid;
    grid-template-columns: 1fr 1px 1fr;
    gap: 20px;
    margin-top: 30px;
    padding: 18px 20px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    text-align: left;
    background: rgba(7, 42, 34, 0.24);
}

.place-detail {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 5px;
}

.place-label {
    color: #bbf7d0;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.place-detail strong {
    overflow-wrap: anywhere;
    color: #fff;
    font-size: 1rem;
    line-height: 1.35;
}

.place-divider {
    width: 1px;
    background: rgba(255, 255, 255, 0.25);
}

.invitation-content {
    padding: clamp(24px, 5vw, 42px) clamp(24px, 6vw, 56px);
}

.invitation-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 150px;
    color: #475569;
    font-weight: 600;
}

.group-invitation {
    padding-bottom: 20px;
    border-bottom: 1px solid #e8eee9;
}

.group-invitation-label {
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.group-invitation h2 {
    margin: 7px 0 10px;
    color: #123f37;
    font-size: clamp(1.55rem, 4vw, 2rem);
    font-weight: 750;
    overflow-wrap: anywhere;
}

.group-type {
    display: inline-block;
    padding: 5px 11px;
    border-radius: 999px;
    color: #166534;
    background: #dcfce7;
    font-size: 0.75rem;
    font-weight: 700;
}

.invitation-copy {
    margin: 20px 0;
    color: #475569;
    line-height: 1.65;
}

.consent-option {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border: 1px solid #dce8df;
    border-radius: 14px;
    background: #f8fbf8;
    cursor: pointer;
}

.consent-option input {
    flex: 0 0 auto;
    margin-top: 4px;
}

.consent-option strong,
.consent-option small {
    display: block;
}

.consent-option strong {
    color: #1e293b;
    font-size: 0.92rem;
    line-height: 1.45;
}

.consent-option small {
    margin-top: 5px;
    color: #64748b;
    line-height: 1.5;
}

.join-button {
    width: 100%;
    margin-top: 20px;
    padding: 12px 20px;
    font-weight: 700;
}

.privacy-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 14px;
    color: #64748b;
    font-size: 0.78rem;
}

.privacy-note .material-icons-round {
    font-size: 16px;
}

.accepted-state {
    padding: 12px 0 4px;
    text-align: center;
}

.accepted-icon {
    width: 64px;
    height: 64px;
    color: #15803d;
    background: #dcfce7;
}

.accepted-icon .material-icons-round {
    font-size: 38px;
}

.accepted-eyebrow {
    color: #15803d;
}

.accepted-state h2 {
    color: #123f37;
    font-weight: 750;
}

.accepted-state > p:not(.accepted-eyebrow) {
    max-width: 480px;
    margin: 12px auto 22px;
    color: #475569;
    line-height: 1.6;
}

.invitation-footer {
    margin: 18px 0 0;
    color: #64748b;
    font-size: 0.8rem;
    letter-spacing: 0.02em;
}

@media (max-width: 520px) {
    .invitation-page {
        justify-content: flex-start;
        padding: 24px 12px;
    }

    .invitation-card {
        border-radius: 18px;
    }

    .invitation-hero {
        padding-top: 30px;
    }

    .invitation-place {
        gap: 12px;
        padding: 15px 13px;
    }
}
</style>
